// EncuestaModal.js — Encuesta de satisfacción (NPS) que se debe responder antes
// de descargar un certificado. Tipos de pregunta soportados:
//   'nps'     → escala 0-10 en círculos (min_label / max_label)
//   'escala'  → escala numérica configurable, por defecto 1-5 (min / max)
//   'matriz'  → varias frases con la misma escala; cada fila se guarda como
//               una respuesta independiente con clave `${p.id}__${fila.id}`
//   'opcion'  → selección única entre `opciones`; con `permite_otro` agrega la
//               opción "Otro" + campo de texto (se guarda en `${p.id}__otro`)
//   'rating'  → 1-5 estrellas
//   'abierta' → texto libre
import React, { useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(2,6,23,0.6); z-index: 2000;
  display: flex; align-items: center; justify-content: center; padding: 20px; overflow-y: auto;
`;
const Panel = styled.div`
  background: #fff; border-radius: 16px; width: 100%; max-width: 560px;
  align-self: center; box-shadow: 0 20px 60px rgba(0,0,0,0.35); overflow: hidden;
`;
const Head = styled.div`
  background: ${p => p.$color || '#0f355b'}; color: #fff; padding: 20px 24px;
`;
const HTitle = styled.div`font-size: 1.25rem; font-weight: 800;`;
const HSub = styled.div`opacity: 0.9; font-size: 0.9rem; margin-top: 4px;`;
const Body = styled.div`padding: 20px 24px; max-height: 65vh; overflow-y: auto;`;
const Q = styled.div`margin-bottom: 22px;`;
const QText = styled.div`font-weight: 700; color: #1e293b; margin-bottom: 10px; font-size: 0.95rem;
  span { color: #ef4444; }`;
const ScaleRow = styled.div`display: flex; gap: 6px; flex-wrap: wrap;`;
const ScaleBtn = styled.button`
  width: 38px; height: 38px; border-radius: 8px; cursor: pointer; font-weight: 700; font-size: 0.9rem;
  border: 1.5px solid ${p => p.$on ? (p.$color || '#0891b2') : '#cbd5e1'};
  background: ${p => p.$on ? (p.$color || '#0891b2') : '#fff'}; color: ${p => p.$on ? '#fff' : '#475569'};
  transition: all 0.12s; &:hover { border-color: ${p => p.$color || '#0891b2'}; }
`;
const ScaleLabels = styled.div`display: flex; justify-content: space-between; font-size: 0.72rem; color: #94a3b8; margin-top: 6px;`;
const Star = styled.button`
  background: none; border: none; cursor: pointer; font-size: 1.9rem; line-height: 1; padding: 0 2px;
  color: ${p => p.$on ? '#f59e0b' : '#d1d5db'}; transition: transform 0.1s; &:hover { transform: scale(1.15); }
`;
const MatrizRow = styled.div`
  padding: 10px 0; border-top: 1px solid #eef2f7;
  &:first-child { border-top: none; }
`;
const MatrizText = styled.div`font-size: 0.88rem; color: #334155; margin-bottom: 8px;`;
const OpcionLabel = styled.label`
  display: flex; align-items: flex-start; gap: 8px; padding: 7px 0; cursor: pointer;
  font-size: 0.88rem; color: #334155; line-height: 1.35;
  input { margin-top: 2px; accent-color: ${p => p.$color || '#0891b2'}; cursor: pointer; }
`;
const OtroInput = styled.input`
  width: 100%; margin: 4px 0 0 24px; padding: 8px 10px; border: 1px solid #cbd5e1;
  border-radius: 8px; font-size: 0.88rem; font-family: inherit;
`;
const TextArea = styled.textarea`
  width: 100%; min-height: 84px; padding: 10px 12px; border: 1px solid #cbd5e1; border-radius: 8px;
  font-size: 0.9rem; font-family: inherit; resize: vertical;
`;
const Foot = styled.div`padding: 16px 24px; border-top: 1px solid #eef2f7; display: flex; justify-content: flex-end; gap: 10px;`;
const Btn = styled.button`
  border: none; border-radius: 8px; padding: 10px 20px; font-weight: 700; cursor: pointer; font-size: 0.9rem;
  background: ${p => p.$primary ? (p.$color || '#0891b2') : '#e2e8f0'}; color: ${p => p.$primary ? '#fff' : '#334155'};
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// Valor que se guarda cuando el usuario elige la opción "Otro".
const OTRO = 'Otro';

/* Escala de color de los círculos (encuesta.escala_colores): el 1 en rojo y el
   último en verde, pasando por el ámbar, sin importar cuántos números tenga la
   escala. La paleta se puede cambiar desde el admin con `paleta_escala`. */
const PALETA_ESCALA = ['#ef5350', '#f7b32b', '#63bb6a'];

const mezclar = (a, b, t) => {
  const hex = (c) => [1, 3, 5].map(i => parseInt(c.slice(i, i + 2), 16));
  const [r1, g1, b1] = hex(a);
  const [r2, g2, b2] = hex(b);
  const v = (x, y) => Math.round(x + (y - x) * t).toString(16).padStart(2, '0');
  return `#${v(r1, r2)}${v(g1, g2)}${v(b1, b2)}`;
};

// Color del valor `i` (0..n-1) dentro de la paleta.
export const colorDeEscala = (i, n, paleta = PALETA_ESCALA) => {
  const p = Array.isArray(paleta) && paleta.length ? paleta : PALETA_ESCALA;
  if (p.length === 1 || n <= 1) return p[0];
  const pos = (i / (n - 1)) * (p.length - 1);
  const k = Math.min(Math.floor(pos), p.length - 2);
  return mezclar(p[k], p[k + 1], pos - k);
};


/* ============================================================
   Modo "paso a paso" (encuesta.paso_a_paso): una pregunta por
   pantalla, con contador, botones Anterior/Siguiente y una pantalla
   final con el botón de descarga. Todo el texto y los colores salen
   de la configuración de la encuesta.
   ============================================================ */
const WOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(2,6,23,0.55); z-index: 2000;
  display: flex; align-items: flex-start; justify-content: center; padding: 24px 16px; overflow-y: auto;
`;
const WPanel = styled.div`
  background: ${p => p.$bg || '#fff'}; width: 100%; max-width: ${p => p.$ancho || '860px'};
  border-radius: ${p => p.$radio || '10px'}; box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  padding: ${p => p.$padding || '2.5rem 2rem 2.2rem'}; text-align: center; margin: auto;
`;

/* Modo embebido: la misma encuesta, pero como bloque dentro de una página en
   vez de flotando sobre ella. Sin velo, sin sombra y sin ancho de modal. */
const WBloque = styled.div`
  width: 100%;
`;
const WPanelBloque = styled.div`
  background: ${p => p.$bg || 'transparent'};
  width: 100%;
  max-width: ${p => p.$ancho || '860px'};
  margin: 0 auto;
  padding: ${p => p.$padding || '0'};
  text-align: center;
`;
const WCerrar = styled.button`
  float: right; background: none; border: none; font-size: 1.4rem; line-height: 1;
  color: #94a3b8; cursor: pointer; &:hover { color: #475569; }
`;
const WTitulo = styled.h2`
  margin: 0 0 0.4rem; font-size: ${p => p.$size || '30px'}; font-weight: 700;
  color: ${p => p.$color || '#0f2f5c'}; font-family: inherit;
`;
const WSub = styled.p`
  margin: 0 0 1.6rem; font-size: ${p => p.$size || '15px'}; color: ${p => p.$color || '#6b7280'};
`;
const WSeccion = styled.h3`
  margin: 0 0 1.1rem; font-size: ${p => p.$size || '22px'}; font-weight: 700;
  color: ${p => p.$color || '#111827'}; font-family: inherit;
`;
const WContador = styled.div`font-size: 0.8rem; color: #94a3b8; margin-bottom: 0.5rem;`;
const WPregunta = styled.div`
  font-size: ${p => p.$size || '17px'}; color: ${p => p.$color || '#0f2f5c'};
  line-height: 1.4; margin-bottom: 1.4rem;
  span.req { color: #ef4444; }
`;
const WFila = styled.div`display: flex; gap: 10px; justify-content: center; flex-wrap: wrap;`;
const WCirculo = styled.button`
  width: ${p => p.$size || '36px'}; height: ${p => p.$size || '36px'}; border-radius: 50%;
  cursor: pointer; font-weight: 700; font-size: 0.9rem; font-family: inherit;
  border: 2px solid ${p => p.$on ? (p.$colorOn || '#3aa79b') : (p.$color || '#0f2f5c')};
  background: ${p => p.$on ? (p.$colorOn || '#3aa79b') : (p.$relleno === false ? 'transparent' : (p.$color || '#0f2f5c'))};
  color: ${p => (p.$on || p.$relleno !== false) ? '#fff' : (p.$color || '#0f2f5c')};
  transition: all 0.12s;
  ${p => p.$on && p.$anillo && `box-shadow: ${p.$anillo};`}
  &:hover { transform: translateY(-1px); }
`;
const WEtiquetas = styled.div`
  display: flex; justify-content: space-between; gap: 1rem;
  max-width: ${p => p.$ancho || '520px'}; margin: 0.6rem auto 0;
  font-size: 0.72rem; color: ${p => p.$color || '#4b7bb5'};
`;
const WMatrizFila = styled.div`
  display: flex; align-items: center; gap: 1rem; padding: ${p => p.$separacion || '0.45rem 0'};
  @media (max-width: 640px) { flex-direction: column; align-items: flex-start; gap: 0.4rem; }
`;
const WMatrizTexto = styled.div`
  flex: 1; text-align: left;
  font-size: ${p => p.$size || '0.9rem'}; color: ${p => p.$color || '#4b5563'};
`;
const WInput = styled.input`
  width: 100%; max-width: 580px; padding: 0.85rem 1rem; border: 1px solid #d7dde5;
  border-radius: 8px; font-size: 0.95rem; font-family: inherit;
  &:focus { outline: none; border-color: ${p => p.$color || '#3aa79b'}; }
`;
const WOpciones = styled.div`
  display: flex; flex-wrap: wrap; gap: 1.2rem; justify-content: center;
  label { display: inline-flex; align-items: center; gap: 0.4rem; cursor: pointer; font-size: 0.98rem; color: #4b5563; }
  input { accent-color: ${p => p.$color || '#3aa79b'}; cursor: pointer; }
`;
const WBotones = styled.div`display: flex; gap: 0.7rem; justify-content: center; margin-top: 2rem;`;
const WBoton = styled.button`
  border: none; border-radius: 999px; cursor: pointer; font-family: inherit;
  padding: ${p => p.$padding || '0.6rem 1.4rem'}; font-size: ${p => p.$size || '0.82rem'};
  font-weight: 700; letter-spacing: 0.4px; text-transform: uppercase;
  background: ${p => p.$bg || '#3aa79b'}; color: ${p => p.$color || '#fff'};
  transition: filter 0.15s, transform 0.15s;
  &:hover { filter: brightness(1.07); transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;
const WFinal = styled.div`
  border: 1px solid ${p => p.$borde || '#d7dde5'}; border-radius: 6px;
  padding: 2rem 1.5rem; margin-top: 0.5rem;
`;
const WFinalTitulo = styled.div`
  font-size: ${p => p.$size || '26px'}; font-weight: 700; color: ${p => p.$color || '#0f2f5c'};
  margin: 0.8rem 0 0.5rem;
`;
const WFinalTexto = styled.div`font-size: 0.95rem; color: #4b5563; margin-bottom: 1rem;`;
const WBotonDescarga = styled.button`
  background: ${p => p.$bg || '#232c5a'}; color: #fff; border: none; border-radius: 4px;
  padding: 0.7rem 1.4rem; font-weight: 700; font-size: 0.92rem; cursor: pointer; font-family: inherit;
  &:hover { filter: brightness(1.1); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const IconoCheck = ({ size = 40, color = '#0f2f5c' }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <rect x="8" y="8" width="32" height="32" rx="4" stroke={color} strokeWidth="4" />
    <path d="M15 25 L22 32 L36 14" stroke={color} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

export const EncuestaWizard = ({ encuesta, color, onSubmit, onClose, onFinalizar, submitting, embebido = false }) => {
  const e = encuesta || {};
  const preguntas = Array.isArray(e.preguntas) ? e.preguntas : [];
  const [resp, setResp] = useState({});
  const [paso, setPaso] = useState(0);
  const [enviado, setEnviado] = useState(false);
  const set = (id, val) => setResp(prev => ({ ...prev, [id]: val }));

  const rango = (p) => {
    const min = Number.isFinite(p.min) ? p.min : 1;
    const max = Number.isFinite(p.max) ? p.max : 5;
    return Array.from({ length: Math.max(0, max - min + 1) }, (_, i) => min + i);
  };
  const opcionesDe = (p) => (Array.isArray(p.opciones) ? p.opciones : [])
    .map((o, i) => (typeof o === 'string' ? { id: `o${i}`, texto: o } : { id: o.id || `o${i}`, texto: o.texto || '' }))
    .filter(o => o.texto !== '');
  const filasDe = (p) => (Array.isArray(p.filas) ? p.filas : [])
    .map((f, i) => (typeof f === 'string' ? { id: `f${i}`, texto: f } : { id: f.id || `f${i}`, texto: f.texto || '' }))
    .filter(f => f.texto !== '');
  const vacio = (v) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

  const p = preguntas[paso];
  const faltaEsta = (() => {
    if (!p || !p.requerida) return false;
    if (p.tipo === 'matriz') return filasDe(p).some(f => vacio(resp[`${p.id}__${f.id}`]));
    if (vacio(resp[p.id])) return true;
    if (p.tipo === 'opcion' && resp[p.id] === OTRO && vacio(resp[`${p.id}__otro`])) return true;
    return false;
  })();

  const enviar = async () => {
    const ok = await onSubmit(resp);
    if (ok !== false) setEnviado(true);
  };

  const colorBoton = e.color_boton || '#3aa79b';
  const colorCirculo = e.color_circulo || color || '#0f2f5c';

  // Escala de color (rojo → verde) por número, opcional. Sin ella, todos los
  // círculos van del color único de siempre.
  const paleta = e.paleta_escala;
  const conColores = (q) => ((q && q.escala_colores !== undefined) ? q.escala_colores : e.escala_colores) === true;
  const colorN = (q, i, n) => (conColores(q) ? colorDeEscala(i, n, paleta) : colorCirculo);
  const anillo = e.anillo_seleccion || '0 0 0 3px #fff, 0 0 0 5px rgba(15,47,92,0.22)';
  const estiloEtiqueta = (q, i, n) => (conColores(q) ? { color: colorDeEscala(i, n, paleta) } : undefined);

  const Envoltura = embebido ? WBloque : WOverlay;
  const Panel = embebido ? WPanelBloque : WPanel;
  const propsEnvoltura = embebido
    ? {}
    : { onClick: (ev) => { if (ev.target === ev.currentTarget && onClose && !enviado) onClose(); } };

  return (
    <Envoltura {...propsEnvoltura}>
      <Panel $bg={e.color_fondo} $ancho={e.ancho} $radio={e.radio} $padding={e.padding}>
        {!embebido && onClose && !enviado && <WCerrar type="button" onClick={onClose} aria-label="Cerrar">×</WCerrar>}
        {!embebido && (
          <>
            <WTitulo $size={e.tamano_titulo} $color={e.color_titulo}>
              {e.titulo_superior || 'Gracias por completar el programa'}
            </WTitulo>
            <WSub $size={e.tamano_subtitulo} $color={e.color_subtitulo}>
              {e.descripcion || 'Tu opinión nos ayuda a seguir ofreciendo experiencias de aprendizaje de alto valor.'}
            </WSub>
          </>
        )}

        {enviado ? (
          <WFinal $borde={e.color_borde_final}>
            {e.icono_final !== false && <IconoCheck color={e.color_titulo || '#0f2f5c'} />}
            <WFinalTitulo $color={e.color_titulo} $size={e.tamano_titulo_final}>
              {e.texto_final || 'Agradecemos tu tiempo y tu evaluación del programa.'}
            </WFinalTitulo>
            <WFinalTexto>{e.texto_final_sub || 'Tu certificado ya está disponible para descarga:'}</WFinalTexto>
            <WBotonDescarga $bg={e.color_boton_descarga} onClick={() => onFinalizar && onFinalizar()}>
              {e.texto_boton_descarga || 'Descargar certificado'}
            </WBotonDescarga>
          </WFinal>
        ) : (
          <>
            <WSeccion $size={e.tamano_seccion} $color={e.color_seccion}>
              {e.titulo || 'Encuesta de satisfacción'}
            </WSeccion>
            <WContador>{`${paso + 1} / ${preguntas.length}`}</WContador>
            {p && (
              <>
                <WPregunta $size={e.tamano_pregunta} $color={e.color_pregunta}>
                  {`${paso + 1}. ${p.texto}`}{p.requerida && <span className="req"> *</span>}
                </WPregunta>

                {p.tipo === 'nps' && (
                  <>
                    <WFila>
                      {Array.from({ length: 10 }, (_, i) => i + 1).map((n, i) => {
                        const col = colorN(p, i, 10);
                        return (
                          <WCirculo key={n} type="button" $on={resp[p.id] === n} $color={col}
                            $colorOn={conColores(p) ? col : colorBoton}
                            $anillo={conColores(p) ? anillo : null}
                            $size={e.tamano_circulo}
                            onClick={() => set(p.id, n)}>{n}</WCirculo>
                        );
                      })}
                    </WFila>
                    <WEtiquetas $ancho={e.ancho_etiquetas}>
                      <span style={estiloEtiqueta(p, 0, 10)}>{`1 = ${p.min_label || 'Nada probable'}`}</span>
                      <span style={estiloEtiqueta(p, 9, 10)}>{`10 = ${p.max_label || 'Extremadamente probable'}`}</span>
                    </WEtiquetas>
                  </>
                )}

                {p.tipo === 'escala' && (
                  <>
                    <WFila>
                      {rango(p).map((n, i, arr) => {
                        const col = colorN(p, i, arr.length);
                        return (
                          <WCirculo key={n} type="button" $on={resp[p.id] === n} $color={col}
                            $colorOn={conColores(p) ? col : colorBoton}
                            $anillo={conColores(p) ? anillo : null}
                            $size={e.tamano_circulo}
                            onClick={() => set(p.id, n)}>{n}</WCirculo>
                        );
                      })}
                    </WFila>
                    {(p.min_label || p.max_label) && (
                      <WEtiquetas $ancho={e.ancho_etiquetas}>
                        <span style={estiloEtiqueta(p, 0, rango(p).length)}>{p.min_label || ''}</span>
                        <span style={estiloEtiqueta(p, rango(p).length - 1, rango(p).length)}>{p.max_label || ''}</span>
                      </WEtiquetas>
                    )}
                  </>
                )}

                {p.tipo === 'matriz' && (
                  <div>
                    {filasDe(p).map(f => {
                      const k = `${p.id}__${f.id}`;
                      return (
                        <WMatrizFila key={f.id} $separacion={e.separacion_filas}>
                          <WMatrizTexto $size={e.tamano_fila} $color={e.color_fila}>{f.texto}</WMatrizTexto>
                          <WFila>
                            {rango(p).map((n, i, arr) => {
                              const col = colorN(p, i, arr.length);
                              return (
                                <WCirculo key={n} type="button" $on={resp[k] === n} $color={col}
                                  $colorOn={conColores(p) ? col : colorBoton} $relleno={false}
                                  $anillo={conColores(p) ? anillo : null}
                                  $size={e.tamano_circulo}
                                  onClick={() => set(k, n)}>{n}</WCirculo>
                              );
                            })}
                          </WFila>
                        </WMatrizFila>
                      );
                    })}
                    {/* Las etiquetas van alineadas bajo la columna de círculos,
                        no bajo el ancho completo de la fila. */}
                    {(p.min_label || p.max_label) && (
                      <WMatrizFila>
                        <WMatrizTexto />
                        <WEtiquetas $ancho="100%" style={{ margin: 0, flex: '0 0 auto' }}>
                          <span style={estiloEtiqueta(p, 0, rango(p).length)}>{p.min_label || ''}</span>
                          <span style={estiloEtiqueta(p, rango(p).length - 1, rango(p).length)}>{p.max_label || ''}</span>
                        </WEtiquetas>
                      </WMatrizFila>
                    )}
                  </div>
                )}

                {p.tipo === 'opcion' && (
                  <WOpciones $color={colorBoton}>
                    {opcionesDe(p).map(o => (
                      <label key={o.id}>
                        <input type="radio" name={p.id} checked={resp[p.id] === o.texto} onChange={() => set(p.id, o.texto)} />
                        <span>{o.texto}</span>
                      </label>
                    ))}
                    {p.permite_otro && (
                      <label>
                        <input type="radio" name={p.id} checked={resp[p.id] === OTRO} onChange={() => set(p.id, OTRO)} />
                        <span>{p.otro_label || 'Otro'}</span>
                      </label>
                    )}
                  </WOpciones>
                )}
                {p.tipo === 'opcion' && p.permite_otro && resp[p.id] === OTRO && (
                  <div style={{ marginTop: 12 }}>
                    <WInput $color={colorBoton} value={resp[`${p.id}__otro`] || ''}
                      onChange={ev => set(`${p.id}__otro`, ev.target.value)} placeholder="¿Cuál?" />
                  </div>
                )}

                {p.tipo === 'rating' && (
                  <WFila>
                    {[1, 2, 3, 4, 5].map(n => (
                      <WCirculo key={n} type="button" $on={(resp[p.id] || 0) >= n} $color={colorCirculo}
                        $colorOn={colorBoton} onClick={() => set(p.id, n)}>{n}</WCirculo>
                    ))}
                  </WFila>
                )}

                {(p.tipo === 'abierta' || !p.tipo) && (
                  <WInput $color={colorBoton} value={resp[p.id] || ''}
                    onChange={ev => set(p.id, ev.target.value)}
                    placeholder={p.placeholder || 'Escriba su respuesta aquí'} />
                )}
              </>
            )}

            <WBotones>
              {paso > 0 && (
                <WBoton type="button" $bg={colorBoton} onClick={() => setPaso(paso - 1)}>
                  {e.texto_anterior || 'Anterior'}
                </WBoton>
              )}
              {paso < preguntas.length - 1 ? (
                <WBoton type="button" $bg={colorBoton} disabled={faltaEsta} onClick={() => setPaso(paso + 1)}>
                  {e.texto_siguiente || 'Siguiente'}
                </WBoton>
              ) : (
                <WBoton type="button" $bg={e.color_boton_enviar || '#4caf7d'} disabled={faltaEsta || submitting} onClick={enviar}>
                  {submitting ? 'Enviando…' : (e.texto_enviar || 'Enviar encuesta')}
                </WBoton>
              )}
            </WBotones>
          </>
        )}
      </Panel>
    </Envoltura>
  );
};

const EncuestaClasica = ({ encuesta, color = '#0f355b', onSubmit, onClose, submitting }) => {

  const [resp, setResp] = useState({});
  const preguntas = Array.isArray(encuesta?.preguntas) ? encuesta.preguntas : [];
  const set = (id, val) => setResp(prev => ({ ...prev, [id]: val }));

  // Escala numérica de la pregunta (por defecto 1-5 en 'escala'/'matriz').
  const rango = (p) => {
    const min = Number.isFinite(p.min) ? p.min : 1;
    const max = Number.isFinite(p.max) ? p.max : 5;
    return Array.from({ length: Math.max(0, max - min + 1) }, (_, i) => min + i);
  };
  // Las opciones admiten strings sueltos o {id, texto}.
  const opcionesDe = (p) => (Array.isArray(p.opciones) ? p.opciones : [])
    .map((o, i) => (typeof o === 'string' ? { id: `o${i}`, texto: o } : { id: o.id || `o${i}`, texto: o.texto || '' }))
    .filter(o => o.texto !== '');
  const filasDe = (p) => (Array.isArray(p.filas) ? p.filas : [])
    .map((f, i) => (typeof f === 'string' ? { id: `f${i}`, texto: f } : { id: f.id || `f${i}`, texto: f.texto || '' }))
    .filter(f => f.texto !== '');

  const vacio = (v) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

  const faltan = preguntas.some(p => {
    if (!p.requerida) return false;
    if (p.tipo === 'matriz') return filasDe(p).some(f => vacio(resp[`${p.id}__${f.id}`]));
    if (vacio(resp[p.id])) return true;
    // "Otro" seleccionado obliga a escribir el detalle.
    if (p.tipo === 'opcion' && resp[p.id] === OTRO && vacio(resp[`${p.id}__otro`])) return true;
    return false;
  });

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}>
      <Panel>
        <Head $color={color}>
          <HTitle>{encuesta?.titulo || 'Encuesta de satisfacción'}</HTitle>
          <HSub>{encuesta?.descripcion || 'Tu opinión nos ayuda a mejorar. Responde para descargar tu certificado.'}</HSub>
        </Head>
        <Body>
          {preguntas.map(p => (
            <Q key={p.id}>
              <QText>{p.texto}{p.requerida && <span> *</span>}</QText>
              {p.tipo === 'nps' && (
                <>
                  <ScaleRow>
                    {Array.from({ length: 11 }, (_, i) => i).map(n => (
                      <ScaleBtn key={n} type="button" $on={resp[p.id] === n} $color={color} onClick={() => set(p.id, n)}>{n}</ScaleBtn>
                    ))}
                  </ScaleRow>
                  <ScaleLabels>
                    <span>{p.min_label || '0'}</span>
                    <span>{p.max_label || '10'}</span>
                  </ScaleLabels>
                </>
              )}
              {p.tipo === 'escala' && (
                <>
                  <ScaleRow>
                    {rango(p).map(n => (
                      <ScaleBtn key={n} type="button" $on={resp[p.id] === n} $color={color} onClick={() => set(p.id, n)}>{n}</ScaleBtn>
                    ))}
                  </ScaleRow>
                  {(p.min_label || p.max_label) && (
                    <ScaleLabels>
                      <span>{p.min_label || ''}</span>
                      <span>{p.max_label || ''}</span>
                    </ScaleLabels>
                  )}
                </>
              )}
              {p.tipo === 'matriz' && (
                <div>
                  {filasDe(p).map(f => {
                    const key = `${p.id}__${f.id}`;
                    return (
                      <MatrizRow key={f.id}>
                        <MatrizText>{f.texto}</MatrizText>
                        <ScaleRow>
                          {rango(p).map(n => (
                            <ScaleBtn key={n} type="button" $on={resp[key] === n} $color={color} onClick={() => set(key, n)}>{n}</ScaleBtn>
                          ))}
                        </ScaleRow>
                      </MatrizRow>
                    );
                  })}
                  {(p.min_label || p.max_label) && (
                    <ScaleLabels>
                      <span>{p.min_label || ''}</span>
                      <span>{p.max_label || ''}</span>
                    </ScaleLabels>
                  )}
                </div>
              )}
              {p.tipo === 'opcion' && (
                <div>
                  {opcionesDe(p).map(o => (
                    <OpcionLabel key={o.id} $color={color}>
                      <input
                        type="radio"
                        name={p.id}
                        checked={resp[p.id] === o.texto}
                        onChange={() => set(p.id, o.texto)}
                      />
                      <span>{o.texto}</span>
                    </OpcionLabel>
                  ))}
                  {p.permite_otro && (
                    <>
                      <OpcionLabel $color={color}>
                        <input type="radio" name={p.id} checked={resp[p.id] === OTRO} onChange={() => set(p.id, OTRO)} />
                        <span>{p.otro_label || 'Otro'}</span>
                      </OpcionLabel>
                      {resp[p.id] === OTRO && (
                        <OtroInput
                          value={resp[`${p.id}__otro`] || ''}
                          onChange={e => set(`${p.id}__otro`, e.target.value)}
                          placeholder="¿Cuál?"
                        />
                      )}
                    </>
                  )}
                </div>
              )}
              {p.tipo === 'rating' && (
                <div>
                  {[1, 2, 3, 4, 5].map(n => (
                    <Star key={n} type="button" $on={(resp[p.id] || 0) >= n} onClick={() => set(p.id, n)} aria-label={`${n} estrellas`}>★</Star>
                  ))}
                </div>
              )}
              {(p.tipo === 'abierta' || !p.tipo) && (
                <TextArea value={resp[p.id] || ''} onChange={e => set(p.id, e.target.value)} placeholder="Escribe tu respuesta…" />
              )}
            </Q>
          ))}
        </Body>
        <Foot>
          {onClose && <Btn type="button" onClick={onClose}>Cancelar</Btn>}
          <Btn type="button" $primary $color={color} disabled={faltan || submitting} onClick={() => onSubmit(resp)}>
            {submitting ? 'Enviando…' : 'Enviar y descargar'}
          </Btn>
        </Foot>
      </Panel>
    </Overlay>
  );
};

// Diseño paso a paso (una pregunta por pantalla) si la encuesta lo pide;
// si no, el modal de siempre.
const EncuestaModal = (props) => (
  props.encuesta?.paso_a_paso === true
    ? <EncuestaWizard {...props} />
    : <EncuestaClasica {...props} />
);

export default EncuestaModal;
